import React from 'react';
import _ from 'lodash';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import {
  Box,
  Button,
  Divider,
  FormControl,
  Paper,
  TextField,
  Typography,
  InputAdornment,
  Stack,
  Checkbox,
  Radio,
  RadioGroup,
  FormControlLabel
} from '@mui/material';
import AccountCircle from '@mui/icons-material/AccountCircle';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { DebounceInput } from 'react-debounce-input';
import { useDispatch } from 'react-redux';
import { useMutation } from 'react-fetching-library';

import { AlertDialogContainer } from '../../../components/others/AlertDialogContainer';
import { setToken, setGroup } from '../../../services/redux-actions';
import { registerAction } from '../../../services/request';
import { useFullScreen } from '../../../services/hooks';

export const RegisterForm = ({ defaultGroup = 'clients' }) => {
  const mediaWidth = useFullScreen() ? '300px' : '450px';
  const dispatch = useDispatch();
  const { mutate } = useMutation(registerAction);
  const [showAlert, setShowAlert] = React.useState(false);
  const [severity, setSeverity] = React.useState('');
  const [title, setTitle] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [checked, setChecked] = React.useState(false);

  const handleChangeChecked = event => {
    setChecked(event.target.checked);
  };

  const validationSchema = Yup.object({
    email: Yup.string().email('Proszę podać poprawny adres e-mail')
      .required('To pole nie może być puste'),
    password1: Yup.string()
      .required('To pole nie może być puste')
      .min(8, 'Hasło musi mieć co najmniej 8 znaków')
      .max(128, 'Hasło może mięc najwyżej 128 znaków'),
    password2: Yup.string()
      .required('To pole nie może być puste')
      .min(8, 'Hasło musi mieć co najmniej 8 znaków')
      .max(128, 'Hasło może mięc najwyżej 128 znaków')
      .oneOf([Yup.ref('password1')], 'Wpisz poprawne hasło')
  });

  const initialValues = {
    email: '',
    password1: '',
    password2: '',
    groups: defaultGroup
  };

  const handleSubmit = async values => {
    const { error, payload } = await mutate(values);
    if (!error) {
      dispatch(setToken(payload.token));
      dispatch(setGroup(payload.user.groups[0]));
    } else {
      setSeverity('error');
      setTitle('Błąd');
      setMessage(payload.message);
      setShowAlert(true);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: values => {
      handleSubmit(values);
    }
  });

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      component={Paper}
      elevation={4}
    >
      {showAlert && (
        <AlertDialogContainer
          severity={severity}
          title={title}
          message={message}
          setShowAlert={setShowAlert}
        />
      )}
      <form onSubmit={formik.handleSubmit} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <FormControl
          style={{
            marginTop: 20,
            marginLeft: 20,
            marginRight: 20,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Typography color="primary" variant="h6">
            Adres e-mail
          </Typography>
          <DebounceInput
            variant="outlined"
            name="email"
            id="email"
            type="text"
            value={formik.values.email}
            onChange={formik.handleChange}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            debounceTimeout={100}
            element={TextField}
            style={{ marginBottom: 10 }}
            autoFocus
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AccountCircle />
                </InputAdornment>
              )
            }}
          />
        </FormControl>
        <FormControl
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginLeft: 20,
            marginRight: 20
          }}
        >
          <Typography color="primary" variant="h6">
            Hasło
          </Typography>
          <DebounceInput
            variant="outlined"
            type="password"
            name="password1"
            id="password1"
            value={formik.values.password1}
            onChange={formik.handleChange}
            error={formik.touched.password1 && Boolean(formik.errors.password1)}
            helperText={formik.touched.password1 && formik.errors.password1}
            debounceTimeout={100}
            element={TextField}
            style={{ marginBottom: 10 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOpenIcon />
                </InputAdornment>
              )
            }}
          />
        </FormControl>
        <FormControl
          style={{
            display: 'flex',
            flexDirection: 'column',
            marginLeft: 20,
            marginRight: 20
          }}
        >
          <Typography color="primary" variant="h6">
            Powtórz hasło
          </Typography>
          <DebounceInput
            variant="outlined"
            type="password"
            name="password2"
            id="password2"
            value={formik.values.password2}
            onChange={formik.handleChange}
            error={formik.touched.password2 && Boolean(formik.errors.password2)}
            helperText={formik.touched.password2 && formik.errors.password2}
            debounceTimeout={100}
            element={TextField}
            style={{ marginBottom: 10 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOpenIcon />
                </InputAdornment>
              )
            }}
          />
        </FormControl>
        <Typography variant="body2" sx={{ width: mediaWidth }}>
          Dokonując rejestracji w Serwisie SUN-SPORT.PL przekazujesz nam swoje dane osobowe. Kliknij <a href="https://sun-sport.pl/informacja-o-przetwarzaniu-danych-osobowych-w-zwiazku-z-rejestracja-uzytkownika-w-serwisie-sun-sport-pl/" target="_blank" rel="noopener noreferrer">TUTAJ</a>, aby zapoznać się ze szczegółowymi informacjami dotyczącymi wykorzystania przez nas tych danych.
        </Typography>
        <Divider style={{ width: mediaWidth, marginTop: 20, marginBottom: 20 }} />
        <Stack direction="row" sx={{ width: mediaWidth, mb: '20px' }} alignItems="flex-start" spacing={2}>
          <Checkbox onChange={handleChangeChecked} checked={checked}/>
          <Typography variant="body2">
          Oświadczam, że zapoznałem(-am) się z udostępnionymi mi informacjami dotyczącymi przetwarzania  przez Jolantę Walas – Zdunek prowadzącą działalność pod firmą Organizator Aktywnego Wypoczynku SUN SPORT Jolanta Walas – Zdunek moich danych osobowych przekazanych przeze mnie w powyższym formularzu rejestracyjnym.
          </Typography>
        </Stack>
        <Button
          color="primary"
          variant="contained"
          type="submit"
          style={{ marginBottom: 20, marginLeft: 20, marginRight: 20 }}
          disabled={!checked}
        >
          Zarejestruj
        </Button>
      </form>
    </Box>
  );
};
